import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import {
  type DirectPriceDraft,
  useDirectPrices,
} from '../../hooks/useDirectPrices/useDirectPrices';
import {
  directPriceFields,
  directPriceSchema,
} from '../../utils/direct-price-fields/direct-price-fields.util';
import { DIRECT_PRICE_SCOPE_IDS } from '../../utils/direct-price-scopes/direct-price-scopes.util';

export interface UseDirectPriceEditorModalLogicInput {
  /** The plan being priced — a serializable modal payload. */
  productKey: string;
  /** What to call it on screen; falls back to the key. */
  productName: string;
  onDone: () => void;
}

export interface UseDirectPriceEditorModalLogic {
  title: string;
  description: string;
  saveLabel: string;
  fields: readonly FieldDef[];
  schema: ReturnType<typeof directPriceSchema>;
  initialValues: DirectPriceDraft;
  /** Held until the prices arrive — RHF reads `defaultValues` exactly once. */
  isLoading: boolean;
  submit: (values: DirectPriceDraft) => Promise<void>;
}

/**
 * One plan's direct price, at every scope it can have one (U5: the modal below
 * is presentation only).
 *
 * It loads `billing.directPrices` itself rather than taking it as modal data,
 * for `useAiRateEditorModalLogic`'s reason: the payload stays a serializable
 * key, and an open dialog keeps agreeing with the server while the board
 * refetches underneath it.
 *
 * ## The boxes hold the OVERRIDE, and the sentences hold the resolution
 *
 * `initialValues` come from `storedFor`, never from `priceFor`: the box is
 * "what this gender's own price is", and an inheriting gender's own price is
 * *nothing*. Seeding it with the inherited number would turn opening the dialog
 * and pressing Save into an override nobody asked for — and, per gender, that is
 * a price that silently stops following the global one for ever.
 */
export function useDirectPriceEditorModalLogic({
  productKey,
  productName,
  onDone,
}: UseDirectPriceEditorModalLogicInput): UseDirectPriceEditorModalLogic {
  const { t } = useTranslation('subscriptions');
  const { locale } = useDirection();
  const toast = useToast();
  const prices = useDirectPrices();

  const { mayManage, savePrices, storedFor, priceFor, currency, state } = prices;

  const initialValues = useMemo<DirectPriceDraft>(
    () =>
      Object.fromEntries(
        DIRECT_PRICE_SCOPE_IDS.map((scope) => [scope, storedFor(scope)[productKey] ?? null]),
      ) as DirectPriceDraft,
    [storedFor, productKey],
  );

  const fields = useMemo(
    () =>
      directPriceFields({
        t,
        locale,
        currency,
        priceFor: (scope) => priceFor(productKey, scope),
        isDisabled: !mayManage,
      }),
    [t, locale, currency, priceFor, productKey, mayManage],
  );

  const submit = useCallback(
    async (values: DirectPriceDraft) => {
      if (!mayManage) {
        // Shaped as an `ApiError` rather than thrown bare: the form renders a
        // thrown error's own message as its failure summary, and an internal
        // token would then reach the screen untranslated (U8). `status: 0` is
        // the literal truth — the request never left the browser.
        const refusal: ApiError = {
          status: 0,
          code: 'forbidden',
          message: t('pricing.board.readOnly'),
        };

        throw refusal;
      }

      await savePrices(productKey, values);
      toast.success(t('pricing.editor.saved'));
      onDone();
    },
    [mayManage, savePrices, productKey, toast, t, onDone],
  );

  return {
    title: t('pricing.editor.title', { name: productName === '' ? productKey : productName }),
    description: t('pricing.editor.description', {
      currency: currency === '' ? t('pricing.unpriced') : currency,
    }),
    saveLabel: t('pricing.editor.save'),
    fields,
    schema: directPriceSchema(),
    initialValues,
    isLoading: state === 'loading',
    submit,
  };
}
